import {
  Box,
  Button,
  Center,
  Flex,
  Group,
  Stack,
  Text,
  Title,
  useMantineColorScheme,
  useMatches,
} from "@mantine/core";
import { useTranslation } from "react-i18next";

import performancechart1 from "@/assets/illustrations/garage/performancechart1.svg";
import performancechart2 from "@/assets/illustrations/garage/performancechart2.svg";
import performancechart3 from "@/assets/illustrations/garage/performancechart3.svg";
import { PlaceholderValue, TextSerif } from "@/components/elements";
import { IconPlus } from "@/components/icons";

import type { FC } from "react";

interface Props {
  onAddCar: (() => void) | undefined;
}

const GarageAggregationEmptyState: FC<Props> = ({ onAddCar }) => {
  const { t } = useTranslation();

  /* Theme */
  const { colorScheme } = useMantineColorScheme();
  const isDark = colorScheme === "dark";

  const isMobile = useMatches({ base: true, md: false });
  const height = isMobile ? 320 : 420;

  const chartStyle = {
    opacity: isDark ? 0.45 : 0.7,
    filter: isDark ? "brightness(0.7)" : undefined,
  };

  return (
    <Box pos="relative" h={height}>
      <Box pos="absolute" style={{ inset: 0, opacity: isDark ? 0.25 : 0.4 }}>
        <Title order={3} pos="absolute" top={-14} left={0}>
          {t("garage.portfolio")}
        </Title>

        <Center
          h={isMobile ? "auto" : "100%"}
          mt={isMobile ? 0 : "lg"}
          style={isMobile ? { position: "absolute", right: 0, bottom: 15, left: 0 } : undefined}
        >
          {isMobile ? (
            <Center w="100%">
              <Flex justify="center" align="center" gap="md" w="100%">
                <PlaceholderValue
                  gap="sm"
                  lines={[
                    { w: "90%", h: 12, color: "secondary" },
                    { w: "80%", h: 10, color: "primary" },
                    { w: "70%", h: 8, color: "secondary" },
                    { w: "50%", h: 7, color: "primary" },
                  ]}
                />
              </Flex>
            </Center>
          ) : (
            <Group justify="space-around" align="flex-start" w="100%">
              <Stack align="start" gap="1em">
                <img src={performancechart1} alt="" width={250} style={chartStyle} />
                <PlaceholderValue
                  w="100%"
                  lines={[
                    { w: "80%", h: 10 },
                    { w: "50%", h: 10, color: "secondary" },
                    { w: "60%", h: 8 },
                  ]}
                />
              </Stack>

              <Stack align="start" gap="1em">
                <img src={performancechart2} alt="" width={250} style={chartStyle} />
                <PlaceholderValue
                  w="100%"
                  lines={[
                    { w: "80%", h: 10 },
                    { w: "50%", h: 10, color: "secondary" },
                    { w: "60%", h: 8 },
                  ]}
                />
              </Stack>

              <Stack align="start" gap="1em">
                <img src={performancechart3} alt="" width={250} style={chartStyle} />
                <PlaceholderValue
                  w="100%"
                  lines={[
                    { w: "80%", h: 10 },
                    { w: "50%", h: 10, color: "secondary" },
                    { w: "60%", h: 8 },
                  ]}
                />
              </Stack>
            </Group>
          )}
        </Center>
      </Box>

      {/* EmptyState */}
      <Center
        pos="absolute"
        top={isMobile ? 0 : 50}
        right={0}
        bottom={0}
        left={0}
        style={{ zIndex: 1 }}
      >
        <Stack align="center" gap="lg" ta="center" maw={360} px={isMobile ? "md" : 0}>
          <Stack gap="xs" align="center">
            <Text fz="h3">{t("garage.yourGarage")}</Text>

            <TextSerif fz="sm" c={isDark ? "#A0A0A0" : "#6A6A84"} maw={300}>
              {t("garage.noCarsYet")}
            </TextSerif>
          </Stack>

          {onAddCar && (
            <Button
              variant="filled"
              onClick={onAddCar}
              w="100% "
              aria-label={t("dashboard.cars.create_button")}
              leftSection={<IconPlus />}
            >
              {t("dashboard.cars.create_button")}
            </Button>
          )}
        </Stack>
      </Center>
    </Box>
  );
};

export default GarageAggregationEmptyState;
